export default function MDVEditPrivacy() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Privacy Policy - Markdown Viewer &amp; Editor
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Effective date: <time dateTime="2026-07-10">July 10, 2026</time>
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>Cosmovex</strong> built the{" "}
            <strong>Markdown Viewer &amp; Editor</strong> (MDVEdit) app as an
            ad-supported application. This Privacy Policy explains what
            information MDVEdit may process, how that information is used, and
            the choices you have when using the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Information We Process
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Markdown files you open:</strong> .md files opened via
              file picker, clipboard, or created in the app are processed locally
              on your device and are not uploaded to our servers, unless you
              explicitly choose to Publish a document (see below).
            </li>
            <li>
              <strong>Published documents (Pro feature):</strong> if you use
              Publish to turn a document into a webpage, that document&apos;s
              content is uploaded to and stored on our servers and becomes
              publicly accessible at the resulting web address until you
              request its removal.
            </li>
            <li>
              <strong>Uploaded images (Pro feature):</strong> if you insert an
              image using the hosted-image option, the image file is uploaded
              to and stored on our servers so it can be embedded via a
              permanent link, and becomes accessible to anyone with that link
              until you request its removal.
            </li>
            <li>
              <strong>App preferences:</strong> your theme choice, language
              preference, and settings are stored locally on your device.
            </li>
            <li>
              <strong>Anonymous account identifier:</strong> Publish and
              hosted-image upload require a Firebase-issued anonymous identifier
              to attribute content to your device and enforce fair-use limits.
              It is not linked to your name, email, or any other personal
              identity.
            </li>
            <li>
              <strong>Usage and ad data:</strong> if ads are enabled, AdMob or
              related Google Mobile Ads services may collect device and
              advertising data as described in Google&apos;s own privacy
              documentation.
            </li>
            <li>
              <strong>Analytics and diagnostics:</strong> Firebase Analytics and
              Firebase Crashlytics may collect app stability, usage patterns, and
              crash reports to improve reliability and user experience.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            How We Use Information
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              To let you view, edit, preview, and export Markdown documents.
            </li>
            <li>To remember your theme, language, and settings.</li>
            <li>
              To improve app performance, reliability, and product quality.
            </li>
            <li>
              To display ads in supported versions of the app, where applicable.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Local Storage</h2>
          <p>
            By default, MDVEdit keeps your data on your device. Markdown files
            and preferences are stored locally and are not uploaded to any
            server. Data remains on your device until you clear it, clear the
            app&apos;s storage, or uninstall the app. This does not apply to
            content you explicitly choose to Publish or images you explicitly
            choose to upload via the hosted-image option — see above.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Publishing and Hosted Images — Acceptable Use
          </h2>
          <p>
            Publish and hosted-image upload are Pro features that place your
            content on public, Cosmovex-operated infrastructure. You must own
            the rights to anything you publish or upload, and may not use these
            features for illegal, infringing, abusive, or harmful content. We
            reserve the right to remove published documents or uploaded images
            that violate this policy, without notice. Both features are subject
            to fair-use rate limits to keep the service available for everyone;
            these limits may change without notice.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Advertising</h2>
          <p>
            MDVEdit may use <strong>Google AdMob</strong> to show banner,
            interstitial, and rewarded ad formats. AdMob may collect and process
            identifiers, device information, approximate location, and ad
            interaction data to deliver and measure ads. You can remove ads via
            an in-app purchase.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            In-App Purchases
          </h2>
          <p>
            MDVEdit offers an optional in-app purchase to remove advertisements.
            Purchases are processed through Google Play or the App Store and are
            subject to their respective terms and privacy policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Third-Party Services
          </h2>
          <p>MDVEdit may rely on third-party services such as:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>Google Play Services</li>
            <li>Google AdMob</li>
            <li>Firebase Analytics</li>
            <li>Firebase Crashlytics</li>
            <li>Firebase Remote Config</li>
            <li>
              Firebase Authentication (anonymous), Cloud Functions, and Cloud
              Storage — used only by the Publish and hosted-image upload Pro
              features
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Sharing</h2>
          <p>
            We do not sell your Markdown files or personal data. Data may be
            shared only with service providers required to operate app features,
            such as advertising, analytics, app distribution, or diagnostics
            infrastructure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Retention</h2>
          <p>
            Local app data remains on your device until you remove it, clear the
            app&apos;s storage, or uninstall the app. Published documents and
            uploaded images remain live on our servers until you request their
            removal by contacting us (see below) — uninstalling the app does not
            remove them, since they are designed to remain accessible at their
            public address. Data collected by third-party services such as
            AdMob and Firebase is retained according to those providers&apos;
            own policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Children&apos;s Privacy
          </h2>
          <p>
            MDVEdit is not intended for children under 13, and we do not
            knowingly collect personal information from children through the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Security</h2>
          <p>
            We take reasonable steps to protect data processed by the app, but no
            method of storage or transmission is completely secure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Your Choices</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              You can uninstall the app to remove all locally stored data from
              your device.
            </li>
            <li>You can purchase ad-free to disable all advertising.</li>
            <li>
              You can manage ad personalization and advertising controls through
              your device and Google settings.
            </li>
            <li>
              You can request removal of a published document or uploaded image
              by contacting us at{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>{" "}
              with the page address or image link.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. When we do, we
            will post the updated version on this page and revise the effective
            date.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Us</h2>
          <p>
            For privacy-related questions about Markdown Viewer &amp; Editor,
            contact{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            .
          </p>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
